import { Menu, Settings, SmartToy } from '@mui/icons-material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Box, IconButton, Toolbar, Tooltip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { isLoggedIn } from '@store/reducer/clientReducer';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useCompanyLogoUrl } from '../../../Helper/logoHelper';
import { MvpAgentChatDialog } from '../../_UnderConstruction/ChatAgentDialog/MvpAgentChatDialog';
import T4loLogoIcon from '../../_UnderConstruction/T4loLogoIcon';
import ThemeEditorPopover from '../../_UnderConstruction/ThemeEditor/ThemeEditorPopover';
import UserButton from '../../Buttons/UserButton/UserButton';
import SearchBox from '../../General/SearchBox/SearchBox';
import { MessageButton } from './MessageButton';
import { NotificationButton } from './NotificationButton';

import './Header.css';

// Wir verwenden theme.palette.primary.main statt useAppTheme

const MemoizedSearchBox = React.memo(SearchBox);
const MemoizedUserButton = React.memo(UserButton);

// Icon buttons in header use the 'header-icon-button' className
// which is styled in the theme file

interface HeaderProps {
    toggleNav: () => void;
    navMode: 'hidden' | 'collapsed' | 'expanded';
}

function Header({ toggleNav, navMode }: HeaderProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const companyName = useSelector((state: AppState) => state.company.name);
    const width = useSelector((state: AppState) => state.windowSize.width);
    const permissions = useSelector((state: AppState) => state.permissions);

    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);
    const loggedIn = useSelector((state: AppState) => isLoggedIn(state.client.secret));
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);
    const isMobile = width <= 700;

    const [chatDialogOpen, setChatDialogOpen] = useState(false);
    const companyLogoUrl = useCompanyLogoUrl(100);

    // const navIsOpen = navMode !== 'hidden';

    return (
        <Box
            sx={{
                bgcolor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${theme.palette.divider}`,
                transition: 'background-color 0.3s, color 0.3s',
                zIndex: 1200,
                position: 'relative',
                ...(isMobile && { padding: '0 8px' }),
            }}
        >
            <Toolbar
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: isMobile ? '0 4px' : '0 8px',
                    minHeight: { xs: '64px', sm: '72px' },
                }}
            >
                {/* Linke Seite - T4LO Logo (Mobile) oder Navigation Button */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isMobile && (
                        <Box
                            onClick={() => navigate(`/${ELinks.DASHBOARD}`)}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                            <T4loLogoIcon width={40} height={40} />
                        </Box>
                    )}
                    {!isMobile && !loggedIn && <T4loLogoIcon width={140} height={40} />}
                    {!isMobile && loggedIn && (
                        <IconButton
                            onClick={toggleNav}
                            sx={{
                                width: { xs: 24, sm: 36 },
                                height: { xs: 24, sm: 36 },
                                minWidth: { xs: 24, sm: 36 },
                                maxWidth: { xs: 24, sm: 36 },
                                display: 'flex',
                                alignItems: 'left',
                                justifyContent: 'center',
                                color: theme.palette.primary.main,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    transform: 'scale(1.05)',
                                },
                            }}
                            size="small"
                        >
                            {navMode === 'expanded' ? <MenuOpenIcon /> : <Menu />}
                        </IconButton>
                    )}
                </Box>

                {/* Mitte - Company Logo (nur Desktop wenn eingeloggt) */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexGrow: 1,
                        flexDirection: 'column',
                    }}
                >
                    {!isMobile && loggedIn && (
                        <Box
                            component="img"
                            src={companyLogoUrl}
                            alt={`Logo ${companyName}`}
                            sx={{
                                maxWidth: 160,
                                height: 'auto',
                                maxHeight: 48,
                                p: 1,
                                objectFit: 'contain',
                                cursor: 'pointer',
                            }}
                            onClick={() => navigate(`/${ELinks.DASHBOARD}`)}
                        />
                    )}
                </Box>

                {/* Rechte Seite - Icons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1 }}>
                    {loggedIn && companyName && !isMobile && <MemoizedSearchBox />}

                    {loggedIn && !isMobile && (
                        <IconButton
                            title={langStrings.chatAgent.aiChatTooltip}
                            onClick={() => setChatDialogOpen(true)}
                            sx={{
                                color: theme.palette.primary.main,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    transform: 'scale(1.05)',
                                },
                            }}
                        >
                            <SmartToy />
                        </IconButton>
                    )}

                    {loggedIn && featureFlags.messages && !isMobile && <MessageButton />}

                    {loggedIn && !isMobile && <NotificationButton />}

                    {loggedIn && !isMobile && (
                        <ThemeEditorPopover
                            showSettingsLink={permissions.admin}
                            size={permissions.admin ? 'medium' : 'small'}
                            className="header-icon-button"
                        />
                    )}

                    {loggedIn && !isMobile && permissions.admin && <SettingsButton />}

                    {!isMobile && <MemoizedUserButton />}

                    {isMobile && loggedIn && (
                        <>
                            <MemoizedUserButton />
                            <IconButton
                                onClick={toggleNav}
                                sx={{
                                    color: theme.palette.primary.main,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            >
                                <Menu />
                            </IconButton>
                        </>
                    )}
                </Box>
            </Toolbar>

            {loggedIn && <MvpAgentChatDialog open={chatDialogOpen} onClose={() => setChatDialogOpen(false)} />}
        </Box>
    );
}

export default Header;

function SettingsButton() {
    const navigate = useNavigate();
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).navigation;
    const width = useSelector((state: AppState) => state.windowSize.width);

    const isMobile = width <= 700;

    return (
        <Tooltip
            title={langStrings.settings}
            placement="bottom"
            disableHoverListener={isMobile || width <= theme.breakpoints.values.sm}
        >
            <IconButton
                size="small"
                onClick={() => {
                    navigate(`/${ELinks.SETTINGS}`);
                }}
                sx={{
                    color: theme.palette.primary.main,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        transform: 'scale(1.05)',
                    },
                }}
            >
                <Settings />
            </IconButton>
        </Tooltip>
    );
}
